/**
 * Session Monitor Service
 * Monitors authentication session and handles automatic logout/refresh
 */

import { AuthTokenManager } from './AuthTokenManager';
import { NotificationService, NotificationType } from './NotificationService';

export interface SessionMonitorConfig {
  checkInterval: number; // How often to check session (ms)
  warningThreshold: number; // Warn user when session expires in X ms
  autoRefresh: boolean; // Automatically refresh tokens
  autoLogout: boolean; // Automatically logout on expiry
}

export class SessionMonitorService {
  private static instance: SessionMonitorService | null = null;
  
  private tokenManager: AuthTokenManager;
  private notificationService: NotificationService;
  private monitorInterval: NodeJS.Timeout | null = null;
  private refreshTimeout: NodeJS.Timeout | null = null;
  
  private config: SessionMonitorConfig = {
    checkInterval: 60 * 1000, // Check every minute
    warningThreshold: 5 * 60 * 1000, // Warn 5 minutes before expiry
    autoRefresh: true,
    autoLogout: true
  };

  private callbacks: {
    onSessionExpiring?: (timeRemaining: number) => void;
    onSessionExpired?: () => void;
    onTokenRefreshed?: () => void;
    onRefreshFailed?: () => void;
  } = {};

  static getInstance(): SessionMonitorService {
    if (!SessionMonitorService.instance) {
      SessionMonitorService.instance = new SessionMonitorService();
    }
    return SessionMonitorService.instance;
  }

  constructor() {
    this.tokenManager = AuthTokenManager.getInstance();
    this.notificationService = NotificationService.getInstance();
  }

  /**
   * Start monitoring the session
   */
  startMonitoring(callbacks?: {
    onSessionExpiring?: (timeRemaining: number) => void;
    onSessionExpired?: () => void;
    onTokenRefreshed?: () => void;
    onRefreshFailed?: () => void;
  }): void {
    if (callbacks) {
      this.callbacks = callbacks;
    }

    // Clear any existing monitoring
    this.stopMonitoring();

    console.log('🔍 Starting session monitoring...');

    // Start periodic checks
    this.monitorInterval = setInterval(() => {
      this.checkSession();
    }, this.config.checkInterval);

    // Schedule initial token refresh if needed
    this.scheduleTokenRefresh();
  }

  /**
   * Stop monitoring the session
   */
  stopMonitoring(): void {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    console.log('🔍 Session monitoring stopped');
  }

  /**
   * Update monitoring configuration
   */
  updateConfig(config: Partial<SessionMonitorConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Restart monitoring with new config
    if (this.monitorInterval) {
      this.startMonitoring(this.callbacks);
    }
  }

  /**
   * Check current session status
   */
  private async checkSession(): Promise<void> {
    const validation = await this.tokenManager.validateTokens();

    if (!validation.isValid) {
      if (validation.isExpired) {
        console.log('⚠️ Session expired');
        this.handleSessionExpired();
      }
      return;
    }

    const timeRemaining = validation.timeUntilExpiry || 0;

    // Check if session is expiring soon
    if (timeRemaining <= this.config.warningThreshold) {
      console.log(`⚠️ Session expiring in ${Math.round(timeRemaining / 1000 / 60)} minutes`);
      this.handleSessionExpiring(timeRemaining);
    }

    // Schedule refresh if needed
    if (validation.needsRefresh && this.config.autoRefresh) {
      this.scheduleTokenRefresh();
    }
  }

  /**
   * Handle session expiring warning
   */
  private handleSessionExpiring(timeRemaining: number): void {
    const minutesRemaining = Math.round(timeRemaining / 1000 / 60);
    
    // Show warning notification
    this.notificationService.addNotification({
      type: NotificationType.WARNING,
      title: 'Session Expiring',
      message: `Your session will expire in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}. Activity will extend your session.`,
      duration: 10000
    });

    // Call callback if provided
    if (this.callbacks.onSessionExpiring) {
      this.callbacks.onSessionExpiring(timeRemaining);
    }
  }

  /**
   * Handle session expired
   */
  private handleSessionExpired(): void {
    // Show expiry notification
    this.notificationService.addNotification({
      type: NotificationType.ERROR,
      title: 'Session Expired',
      message: 'Your session has expired. Please log in again.',
      duration: 8000
    });

    // Call callback if provided
    if (this.callbacks.onSessionExpired) {
      this.callbacks.onSessionExpired();
    }

    // Auto logout if configured
    if (this.config.autoLogout) {
      this.tokenManager.clearTokens();
    }

    // Stop monitoring
    this.stopMonitoring();
  }

  /**
   * Schedule automatic token refresh
   */
  private async scheduleTokenRefresh(): Promise<void> {
    // Clear existing refresh timeout
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    const validation = await this.tokenManager.validateTokens();
    
    if (!validation.isValid || !validation.timeUntilExpiry) {
      return;
    }

    // Schedule refresh 5 minutes before expiry
    const refreshTime = Math.max(0, validation.timeUntilExpiry - (5 * 60 * 1000));
    
    console.log(`⏰ Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);
    
    this.refreshTimeout = setTimeout(async () => {
      await this.attemptTokenRefresh();
    }, refreshTime);
  }

  /**
   * Attempt to refresh the token
   */
  private async attemptTokenRefresh(): Promise<void> {
    try {
      console.log('🔄 Attempting automatic token refresh...');
      
      const newTokens = await this.tokenManager.refreshAccessToken();
      
      if (newTokens) {
        console.log('✅ Token refreshed successfully');
        
        // Show success notification
        this.notificationService.addNotification({
          type: NotificationType.SUCCESS,
          title: 'Session Extended',
          message: 'Your session has been automatically extended.',
          duration: 3000
        });

        // Call callback if provided
        if (this.callbacks.onTokenRefreshed) {
          this.callbacks.onTokenRefreshed();
        }

        // Schedule next refresh
        this.scheduleTokenRefresh();
      } else {
        throw new Error('Token refresh returned null');
      }
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      
      // Show failure notification
      this.notificationService.addNotification({
        type: NotificationType.ERROR,
        title: 'Session Refresh Failed',
        message: 'Unable to extend your session. Please log in again.',
        duration: 8000
      });

      // Call callback if provided
      if (this.callbacks.onRefreshFailed) {
        this.callbacks.onRefreshFailed();
      }

      // Handle as expired session
      this.handleSessionExpired();
    }
  }

  /**
   * Manually refresh token
   */
  async refreshToken(): Promise<boolean> {
    try {
      const newTokens = await this.tokenManager.refreshAccessToken();
      
      if (newTokens) {
        // Reschedule automatic refresh
        this.scheduleTokenRefresh();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      return false;
    }
  }

  /**
   * Get session status
   */
  async getSessionStatus(): Promise<{
    isActive: boolean;
    timeRemaining: number;
    timeRemainingFormatted: string;
    needsRefresh: boolean;
    isExpiring: boolean;
  }> {
    const validation = await this.tokenManager.validateTokens();
    const timeRemaining = validation.timeUntilExpiry || 0;
    const timeRemainingFormatted = await this.tokenManager.getTimeUntilExpiryFormatted();
    
    return {
      isActive: validation.isValid,
      timeRemaining,
      timeRemainingFormatted,
      needsRefresh: validation.needsRefresh,
      isExpiring: timeRemaining <= this.config.warningThreshold && timeRemaining > 0
    };
  }

  /**
   * Extend session (call this on user activity)
   */
  async extendSession(): Promise<void> {
    // In a real implementation, this might make an API call to extend the session
    // For now, we'll just refresh the token if it's close to expiry
    const validation = await this.tokenManager.validateTokens();
    
    if (validation.needsRefresh) {
      await this.attemptTokenRefresh();
    }
  }

  /**
   * Get monitoring status
   */
  isMonitoring(): boolean {
    return this.monitorInterval !== null;
  }

  /**
   * Get debug information
   */
  getDebugInfo(): Record<string, any> {
    return {
      isMonitoring: this.isMonitoring(),
      config: this.config,
      sessionStatus: this.getSessionStatus(),
      tokenInfo: this.tokenManager.getDebugInfo()
    };
  }
}

export default SessionMonitorService;